import { Navigation } from '@/components/Navigation';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

const Terms = () => {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navigation />
      
      <div className="container py-8 max-w-4xl flex-1">
        <Card className="clean-card">
          <CardHeader>
            <CardTitle className="text-3xl font-bold">Terms of Use</CardTitle>
            <p className="text-sm text-muted-foreground">
              Effective Date: 17 of September 2025<br />
              Last Updated: 17 of September 2025
            </p>
          </CardHeader>
          
          <CardContent className="space-y-6">
            <section>
              <h2 className="text-xl font-semibold mb-3">1. Acceptance of Terms</h2>
              <p className="text-muted-foreground">
                By accessing, creating an account, or using <strong>HiStage</strong> ("the Platform"), you agree to these <strong>Terms of Use</strong> and our <strong>Privacy Policy</strong>.
                If you do not agree, please do not use the Platform.
              </p>
              <p className="text-muted-foreground mt-2">
                These Terms apply to all users of HiStage — including <strong>Creators, Artists, Fans, Viewers, and Brand Partners</strong>.
              </p>
            </section>

            <Separator />

            <section>
              <h2 className="text-xl font-semibold mb-3">2. About HiStage</h2>
              <p className="text-muted-foreground">
                HiStage ("we," "our," "us") is a live, interactive entertainment platform that allows <strong>Creators and Artists</strong> to perform and monetize their content, while <strong>Fans</strong> can engage, vote, and support through virtual interactions.
                We operate globally, connecting creators and audiences in real time.
              </p>
            </section>

            <Separator />

            <section>
              <h2 className="text-xl font-semibold mb-3">3. Eligibility</h2>
              <p className="text-muted-foreground mb-2">To use HiStage:</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                <li>You must be <strong>at least 16 years old</strong>, or the age of majority in your jurisdiction.</li>
                <li>If you are under 18, you must have <strong>parental or guardian consent</strong>.</li>
                <li>You must provide accurate account information and comply with all applicable laws.</li>
              </ul>
              <p className="text-muted-foreground mt-2">
                HiStage reserves the right to <strong>suspend or terminate</strong> any account that violates these terms.
              </p>
            </section>

            <Separator />

            <section>
              <h2 className="text-xl font-semibold mb-3">4. User Accounts</h2>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                <li>You are responsible for maintaining the <strong>confidentiality of your account</strong> and password.</li>
                <li>You agree not to share your login credentials or impersonate any person or entity.</li>
                <li>You must notify us immediately of any unauthorized account access.</li>
              </ul>
              <p className="text-muted-foreground mt-2">
                HiStage is not responsible for losses due to unauthorized use of your account.
              </p>
            </section>

            <Separator />

            <section>
              <h2 className="text-xl font-semibold mb-3">5. Creator & Artist Responsibilities</h2>
              <p className="text-muted-foreground mb-2">Creators and Artists agree to:</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                <li>Stream or upload <strong>original content</strong> that they own or have permission to use.</li>
                <li>Avoid uploading or broadcasting any illegal, violent, hateful, or adult material.</li>
                <li>Comply with <strong>copyright, music licensing, and local broadcasting laws</strong>.</li>
                <li>Respect fans and fellow creators — no harassment, hate speech, or spam.</li>
              </ul>
              <p className="text-muted-foreground mt-2">
                HiStage reserves the right to <strong>remove content or suspend creators</strong> who violate these standards.
              </p>
            </section>

            <Separator />

            <section>
              <h2 className="text-xl font-semibold mb-3">6. Fan & Viewer Responsibilities</h2>
              <p className="text-muted-foreground mb-2">Fans and viewers agree to:</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                <li>Use HiStage respectfully and not engage in trolling, harassment, or hate speech.</li>
                <li>Not misuse payment features, including <strong>StageCoins, tipping, or voting</strong>.</li>
                <li>Not attempt to manipulate rankings, leaderboards, or rewards.</li>
              </ul>
              <p className="text-muted-foreground mt-2">
                All interactions, votes, and tips are <strong>final</strong> and <strong>non-refundable</strong> unless stated otherwise.
              </p>
            </section>

            <Separator />

            <section>
              <h2 className="text-xl font-semibold mb-3">7. Virtual Currency — StageCoins</h2>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                <li><strong>StageCoins</strong> are the platform's in-app currency.</li>
                <li>Coins can be purchased through authorized payment channels.</li>
                <li>Coins are <strong>non-transferable</strong> and <strong>non-refundable</strong> except where required by law.</li>
                <li>Coins cannot be exchanged for real money outside the platform.</li>
                <li>Creators receive payouts based on fan tips and votes after applicable fees.</li>
              </ul>
              <p className="text-muted-foreground mt-2">
                HiStage may adjust pricing or conversion rates at any time.
              </p>
            </section>

            <Separator />

            <section>
              <h2 className="text-xl font-semibold mb-3">8. Payments & Payouts</h2>
              
              <h3 className="text-lg font-semibold mt-4 mb-2">For Creators:</h3>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                <li>HiStage pays creators <strong>80%</strong> of revenue earned from fan tips, votes, and unlockables.</li>
                <li>Payments are processed through approved gateways (e.g., Stripe, Payoneer).</li>
                <li>Payouts may be subject to processing fees, taxes, and verification steps.</li>
                <li>Fraudulent or chargeback activity may result in payout suspension.</li>
              </ul>

              <h3 className="text-lg font-semibold mt-4 mb-2">For Fans:</h3>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                <li>All purchases of StageCoins are <strong>final</strong>.</li>
                <li>HiStage is not liable for unauthorized transactions caused by user negligence.</li>
              </ul>
            </section>

            <Separator />

            <section>
              <h2 className="text-xl font-semibold mb-3">9. Prohibited Activities</h2>
              <p className="text-muted-foreground mb-2">You may <strong>not</strong>:</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                <li>Post or broadcast sexually explicit, hateful, violent, or unlawful content.</li>
                <li>Engage in any form of harassment, discrimination, or bullying.</li>
                <li>Use the platform to advertise unrelated products or services.</li>
                <li>Attempt to hack, reverse-engineer, or exploit the system.</li>
                <li>Use bots or automation to manipulate engagement.</li>
              </ul>
              <p className="text-muted-foreground mt-2">
                Violation may lead to <strong>account suspension or permanent ban</strong>.
              </p>
            </section>

            <Separator />

            <section>
              <h2 className="text-xl font-semibold mb-3">10. Intellectual Property</h2>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                <li>All content uploaded or streamed remains the <strong>property of its creator</strong>.</li>
                <li>By posting on HiStage, you grant us a <strong>non-exclusive, royalty-free license</strong> to display, distribute, and promote your content for platform functionality and marketing.</li>
                <li>The HiStage brand, logo, and software are protected under trademark and copyright laws.</li>
              </ul>
            </section>

            <Separator />

            <section>
              <h2 className="text-xl font-semibold mb-3">11. Termination</h2>
              <p className="text-muted-foreground mb-2">HiStage may suspend or terminate any account that:</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                <li>Violates these Terms</li>
                <li>Engages in fraud, abuse, or illegal activity</li>
                <li>Harms the community experience</li>
              </ul>
              <p className="text-muted-foreground mt-2">
                Users may terminate their accounts anytime by contacting support.
              </p>
            </section>

            <Separator />

            <section>
              <h2 className="text-xl font-semibold mb-3">12. Limitation of Liability</h2>
              <p className="text-muted-foreground mb-2">
                HiStage provides the platform <strong>"as is"</strong> without warranties of any kind.
                We do not guarantee continuous access, error-free service, or specific earnings.
                To the fullest extent permitted by law, HiStage is <strong>not liable</strong> for:
              </p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                <li>Loss of profits or data</li>
                <li>Service interruptions</li>
                <li>User disputes or content issues</li>
              </ul>
            </section>

            <Separator />

            <section>
              <h2 className="text-xl font-semibold mb-3">13. Indemnification</h2>
              <p className="text-muted-foreground">
                You agree to indemnify and hold harmless HiStage, its affiliates, and employees from any claims, damages, or expenses arising from your use of the platform or violation of these Terms.
              </p>
            </section>

            <Separator />

            <section>
              <h2 className="text-xl font-semibold mb-3">14. Changes to Terms</h2>
              <p className="text-muted-foreground">
                HiStage may update these Terms at any time.
                We will notify users via email or in-app notice before major updates.
                Continued use of HiStage after updates means you accept the new Terms.
              </p>
            </section>

            <Separator />

            <section>
              <h2 className="text-xl font-semibold mb-3">15. Contact Information</h2>
              <p className="text-muted-foreground">
                If you have any questions or concerns about these Terms, contact:
              </p>
              <p className="text-muted-foreground mt-2">
                📧 <strong>support@histage.com</strong><br />
                🌐 <strong>www.histage.com</strong>
              </p>
            </section>

            <Separator />

            <section className="bg-muted/50 p-6 rounded-lg">
              <h2 className="text-xl font-semibold mb-3">⚡ TL;DR (Plain English Summary)</h2>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground ml-4">
                <li>Be respectful.</li>
                <li>Don't post illegal or stolen content.</li>
                <li>Coins = digital fun money (not cash).</li>
                <li>Creators earn, fans engage.</li>
                <li>We're here to make entertainment interactive, not chaotic.</li>
              </ul>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Terms;
